export type Vec3 = [number, number, number];

export interface CubeLut {
  title: string;
  dimension: number;
  domainMin: Vec3;
  domainMax: Vec3;
  values: Float32Array;
  entries: number;
  fileName: string;
  fileSize: number;
}

export interface ColorPatch {
  name: string;
  rgb: Vec3;
}

export const colorPatches: ColorPatch[] = [
  { name: "black", rgb: [0, 0, 0] },
  { name: "shadow", rgb: [32, 34, 38] },
  { name: "skin", rgb: [194, 130, 92] },
  { name: "red", rgb: [198, 43, 56] },
  { name: "green", rgb: [50, 148, 92] },
  { name: "cyan", rgb: [37, 142, 165] },
  { name: "blue", rgb: [54, 84, 188] },
  { name: "yellow", rgb: [224, 184, 64] },
  { name: "white", rgb: [238, 238, 232] }
];

export function parseCubeLut(text: string, fileName = "untitled.cube", fileSize = text.length): CubeLut {
  let title = fileName.replace(/\.cube$/i, "");
  let dimension = 0;
  let domainMin: Vec3 = [0, 0, 0];
  let domainMax: Vec3 = [1, 1, 1];
  const values: number[] = [];

  const lines = text.split(/\r?\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith("#")) {
      const titleMatch = line.match(/^#\s*title\s*:\s*(.+)$/i);
      if (titleMatch?.[1]) title = titleMatch[1].trim();
      continue;
    }

    const parts = line.split(/\s+/);
    const key = parts[0].toUpperCase();

    if (key === "TITLE") {
      title = line.replace(/^TITLE\s+/i, "").replace(/^"|"$/g, "").trim();
      continue;
    }
    if (key === "LUT_3D_SIZE") {
      dimension = Number.parseInt(parts[1], 10);
      continue;
    }
    if (key === "DOMAIN_MIN" && parts.length >= 4) {
      domainMin = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
      continue;
    }
    if (key === "DOMAIN_MAX" && parts.length >= 4) {
      domainMax = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
      continue;
    }

    if (parts.length >= 3) {
      const r = Number(parts[0]);
      const g = Number(parts[1]);
      const b = Number(parts[2]);
      if ([r, g, b].every(Number.isFinite)) {
        values.push(clamp01(r), clamp01(g), clamp01(b));
      }
    }
  }

  if (!dimension || dimension < 2 || dimension > 128) {
    throw new Error("Missing or unsupported LUT_3D_SIZE. Expected a 3D .cube LUT.");
  }

  const expected = dimension * dimension * dimension * 3;
  if (values.length !== expected) {
    throw new Error(`Invalid LUT entries. Expected ${expected / 3}, got ${values.length / 3}.`);
  }

  return {
    title,
    dimension,
    domainMin,
    domainMax,
    values: new Float32Array(values),
    entries: values.length / 3,
    fileName,
    fileSize
  };
}

export function applyLutToRgb(rgb: Vec3, lut: CubeLut, strength: number): Vec3 {
  const input: Vec3 = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
  const mapped = sampleLut(input, lut);
  const mix = clamp01(strength);
  return [
    Math.round(lerp(input[0], mapped[0], mix) * 255),
    Math.round(lerp(input[1], mapped[1], mix) * 255),
    Math.round(lerp(input[2], mapped[2], mix) * 255)
  ];
}

export function applyLutToImageData(source: ImageData, lut: CubeLut | null, strength: number): ImageData {
  const output = new ImageData(new Uint8ClampedArray(source.data), source.width, source.height);
  if (!lut) return output;

  const data = output.data;
  const mix = clamp01(strength);
  for (let i = 0; i < data.length; i += 4) {
    const input: Vec3 = [data[i] / 255, data[i + 1] / 255, data[i + 2] / 255];
    const mapped = sampleLut(input, lut);
    data[i] = Math.round(lerp(input[0], mapped[0], mix) * 255);
    data[i + 1] = Math.round(lerp(input[1], mapped[1], mix) * 255);
    data[i + 2] = Math.round(lerp(input[2], mapped[2], mix) * 255);
  }
  return output;
}

export function sampleLut(input: Vec3, lut: CubeLut): Vec3 {
  const size = lut.dimension;
  const maxIndex = size - 1;
  const x = clamp01(input[0]) * maxIndex;
  const y = clamp01(input[1]) * maxIndex;
  const z = clamp01(input[2]) * maxIndex;

  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const z0 = Math.floor(z);
  const x1 = Math.min(x0 + 1, maxIndex);
  const y1 = Math.min(y0 + 1, maxIndex);
  const z1 = Math.min(z0 + 1, maxIndex);
  const tx = x - x0;
  const ty = y - y0;
  const tz = z - z0;

  const c000 = getLutPoint(lut, x0, y0, z0);
  const c100 = getLutPoint(lut, x1, y0, z0);
  const c010 = getLutPoint(lut, x0, y1, z0);
  const c110 = getLutPoint(lut, x1, y1, z0);
  const c001 = getLutPoint(lut, x0, y0, z1);
  const c101 = getLutPoint(lut, x1, y0, z1);
  const c011 = getLutPoint(lut, x0, y1, z1);
  const c111 = getLutPoint(lut, x1, y1, z1);

  const c00 = mix3(c000, c100, tx);
  const c10 = mix3(c010, c110, tx);
  const c01 = mix3(c001, c101, tx);
  const c11 = mix3(c011, c111, tx);
  const c0 = mix3(c00, c10, ty);
  const c1 = mix3(c01, c11, ty);
  return mix3(c0, c1, tz);
}

export function summarizeLut(lut: CubeLut | null): string {
  if (!lut) return "Upload a .cube LUT to inspect its color transform locally.";

  const neutral: Vec3 = [128, 128, 128];
  const skin: Vec3 = [194, 130, 92];
  const sky: Vec3 = [72, 118, 190];
  const mappedNeutral = applyLutToRgb(neutral, lut, 1);
  const mappedSkin = applyLutToRgb(skin, lut, 1);
  const mappedSky = applyLutToRgb(sky, lut, 1);

  const neutralLift = average(mappedNeutral) - average(neutral);
  const warmth = mappedSkin[0] - mappedSkin[2] - (skin[0] - skin[2]);
  const blueShift = mappedSky[2] - sky[2];

  const tone = neutralLift > 10 ? "lifts midtones" : neutralLift < -10 ? "compresses midtones" : "keeps midtones controlled";
  const temp = warmth > 8 ? "warmer skin response" : warmth < -8 ? "cooler skin response" : "balanced skin response";
  const blue = blueShift > 8 ? "boosted blues" : blueShift < -8 ? "muted blues" : "restrained blues";

  return `${lut.title} uses a ${lut.dimension}-grid transform: ${tone}, ${temp}, ${blue}.`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function getLutPoint(lut: CubeLut, x: number, y: number, z: number): Vec3 {
  const size = lut.dimension;
  const index = ((z * size * size) + (y * size) + x) * 3;
  return [lut.values[index], lut.values[index + 1], lut.values[index + 2]];
}

function mix3(a: Vec3, b: Vec3, t: number): Vec3 {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

function average(rgb: Vec3): number {
  return (rgb[0] + rgb[1] + rgb[2]) / 3;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
